import { useCallback } from 'react';
import { useChatContext } from '../context/ChatContext';
import { Message, TypingData, ConnectionData } from '../types';

/**
 * Custom hook that provides action creators for chat state updates
 */
export const useChatActions = () => {
  const { dispatch } = useChatContext();

  const addMessage = useCallback((message: Message) => {
    dispatch({ type: 'ADD_MESSAGE', payload: message });
  }, [dispatch]);

  const setTyping = useCallback((typingData: TypingData) => {
    dispatch({ type: 'SET_TYPING', payload: typingData });
  }, [dispatch]);

  const setConnectionStatus = useCallback((connectionData: ConnectionData) => {
    dispatch({ type: 'SET_CONNECTION_STATUS', payload: connectionData });
  }, [dispatch]);

  const toggleLayoutMode = useCallback(() => {
    // Cycle through the three modes
    dispatch({ type: 'SET_LAYOUT_MODE', payload: 'popup' }); // Default toggle behavior
  }, [dispatch]);

  const startRecording = useCallback(() => {
    dispatch({ type: 'START_RECORDING' });
  }, [dispatch]);

  const stopRecording = useCallback(() => {
    dispatch({ type: 'STOP_RECORDING' });
  }, [dispatch]);

  const queueAudio = useCallback((audioData: string) => {
    dispatch({ type: 'QUEUE_AUDIO', payload: audioData });
  }, [dispatch]);

  const dismissIntro = useCallback(() => {
    dispatch({ type: 'DISMISS_INTRO' });
  }, [dispatch]);

  const syncMessages = useCallback((messages: Message[]) => {
    dispatch({ type: 'SYNC_MESSAGES', payload: messages });
  }, [dispatch]);

  const setLastSyncTime = useCallback((time: Date) => {
    dispatch({ type: 'SET_LAST_SYNC_TIME', payload: time });
  }, [dispatch]);

  const setInitialMessage = useCallback((message: Message) => {
    dispatch({ type: 'SET_INITIAL_MESSAGE', payload: message });
  }, [dispatch]);

  const updateMessageContent = useCallback((messageId: string, content: string, isThinking?: boolean) => {
    dispatch({ 
      type: 'UPDATE_MESSAGE_CONTENT', 
      payload: { messageId, content, isThinking } 
    });
  }, [dispatch]);

  const completeMessageStream = useCallback((messageId: string) => {
    dispatch({ 
      type: 'COMPLETE_MESSAGE_STREAM', 
      payload: { messageId } 
    });
  }, [dispatch]);

  return {
    addMessage,
    setTyping,
    setConnectionStatus,
    toggleLayoutMode,
    startRecording,
    stopRecording,
    queueAudio,
    dismissIntro,
    syncMessages,
    setLastSyncTime,
    setInitialMessage,
    updateMessageContent,
    completeMessageStream,
  };
};